import { cn } from "@/lib/utils/cn";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2Icon } from "lucide-react";
import * as React from "react";

const buttonVariants = cva(
  "focus-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium transition-all disabled:pointer-events-none active:scale-95 disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 aria-invalid:focus-visible:ring-0 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90",
        success: "bg-success text-success-foreground shadow-xs hover:bg-success/90",
        warning:
          "border border-warning/60 bg-warning/10 text-warning-text shadow-xs hover:bg-warning/30 dark:border-warning/50 dark:bg-warning/8 dark:hover:bg-warning/20",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-sm px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-sm px-6 has-[>svg]:px-4",
        xxs: "h-5 px-2 has-[>svg]:px-1.5 text-[11px]",
        xs: "h-6 px-2.5 has-[>svg]:px-2 text-xs",
        // Square icon-only sizes. Each matches the height of the text size it's
        // named after (icon-xs ↔ xs, icon-sm ↔ sm, icon ↔ default) so an icon
        // button lines up with text buttons of the same size in a row.
        "icon-xxs": "size-5",
        "icon-xs": "size-6",
        "icon-sm": "size-8",
        icon: "size-9",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps =
  & React.ComponentProps<"button">
  & VariantProps<typeof buttonVariants>
  & {
    /** Render as another element (e.g. a router Link) instead of a <button>. */
    render?: useRender.RenderProp;
    loading?: boolean;
  };

const Button: React.FC<ButtonProps> & {
  variants: typeof buttonVariants;
} = ({
  render,
  children,
  className,
  disabled = false,
  loading = false,
  size,
  type = "button",
  variant,
  ref,
  ...props
}) => {
  // When loading: if the first child is an icon (raw <svg> or a self-closing
  // element like a lucide-react icon — anything with no children of its own),
  // the spinner replaces it. Otherwise the spinner is prepended.
  const childArray = React.Children.toArray(children);
  const firstChild = childArray[0];
  const hasLeadingIcon = React.isValidElement(firstChild)
    && (firstChild.type === "svg"
      || (firstChild.props as { children?: React.ReactNode; }).children == null);
  const renderedChildren = loading && hasLeadingIcon ? childArray.slice(1) : children;

  // `type` must live on the render element, not in `props` below: useRender
  // gives the render element's own props precedence in its merge, so a `type`
  // in `props` loses to the element's. A hardcoded `<button type="button" />`
  // here is what silently downgraded every `type="submit"` button back to
  // "button" and broke form submission. We branch on the (already typed) `type`
  // so each arm is a string literal — `button-has-type` is syntactic and only
  // recognizes literals, not the inferred prop type.
  const defaultButton = type === "submit"
    ? <button type="submit" />
    : type === "reset"
    ? <button type="reset" />
    : <button type="button" />;

  return useRender({
    render: render ?? defaultButton,
    ref,
    props: {
      "data-slot": "button",
      className: cn(buttonVariants({ variant, size, className })),
      disabled: loading || disabled,
      ...props,
      // A custom `render` element controls its own composition, so pass its
      // children straight through (matching the old asChild behavior — no
      // injected spinner). The default <button> gets the loading spinner.
      children: render
        ? children
        : (
          <>
            {loading && <Loader2Icon className="h-4 w-4 animate-spin" />}
            {renderedChildren}
          </>
        ),
    },
  });
};

Button.variants = buttonVariants;

/**
 * Small count pill for use inside a Button, ButtonGroup, or
 * DropdownMenu.Item. Inherits the parent's color via `bg-current/20` so it
 * reads against any parent (success, destructive, popover). Spacing comes
 * from the parent's own `gap` — `ButtonBadge` adds no margin of its own so
 * it behaves consistently across button sizes.
 */
const ButtonBadge: React.FC<
  React.ComponentProps<"span"> & { children: React.ReactNode; }
> = ({ children, className, ...props }) => (
  <span
    className={cn(
      "inline-flex min-w-[1.25rem] items-center justify-center rounded-sm bg-current/20 px-1.5 text-[10px] font-semibold tabular-nums",
      className,
    )}
    {...props}
  >
    {children}
  </span>
);

export { Button, ButtonBadge };
